import dayjs from "dayjs";
import { trainingAPI } from "@api/TrainingAPI";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import * as api from "../api/statusTrackerV2Api";
import { getMockMonthView, getMockStatuses } from "../api/mockMonthData";
import { getDatesToQuery, monthKeyOf } from "./utils";
import {
    ST_SET_LOADING,
    ST_SET_MONTH_GRID,
    ST_SET_STATUSES,
    ST_SET_FLAG_METADATA,
    ST_PATCH_CELL_STATUS,
    ST_SET_V2_ENABLED,
    ST_SET_TAGGING_MODE,
    ST_ADD_COACH_FLAG,
    ST_ADD_COACH_FLAGS,
    ST_REMOVE_COACH_FLAG,
    ST_REMOVE_COACH_FLAGS,
} from "./constants";

// Data source: "v2" hits the real backend (/status-tracker/v2/* + the unchanged v1 select-status
// & flag-metadata endpoints); "mock" runs fully offline for UI-only work. Flip here.
export const DATA_SOURCE = "v2";
const USE_MOCK = DATA_SOURCE === "mock";

const rangeOf = (selectedMonth) => {
    const dates = getDatesToQuery(selectedMonth);
    return { start: dates[0], end: dates[dates.length - 1] };
};

const errMsg = (e, fallback) => {
    const data = e?.response?.data;
    if (typeof data === "string" && data.trim()) return data;
    if (data?.error) return data.error;
    return fallback;
};

// `silentGate` — the embedder reacts to the V2 gate itself (the Overview falls back to V1),
// so the "not enabled" warning would be noise on top of a view that still works.
export const createActions = (dispatch, { silentGate = false } = {}) => {
    const setStatuses = (statuses) => dispatch({ type: ST_SET_STATUSES, payload: statuses });
    const patchCell = (uniqueKey, status) =>
        dispatch({ type: ST_PATCH_CELL_STATUS, payload: { uniqueKey, status } });

    const refreshStatusTypes = async (selectedMonth) => {
        if (USE_MOCK) return;
        try {
            const { start, end } = rangeOf(selectedMonth);
            setStatuses(await api.getStatusTypes(start, end));
        } catch (e) {
            message.error(errMsg(e, "Failed to refresh status types."));
        }
    };

    const fetchMonthGrid = async ({ players, selectedMonth, keyMetrics }) => {
        dispatch({ type: ST_SET_LOADING, payload: true });
        try {
            if (USE_MOCK) {
                dispatch({ type: ST_SET_STATUSES, payload: getMockStatuses() });
                dispatch({
                    type: ST_SET_MONTH_GRID,
                    payload: { monthKey: monthKeyOf(selectedMonth), cells: getMockMonthView(players, selectedMonth, keyMetrics) },
                });
            } else {
                const { start, end } = rangeOf(selectedMonth);
                const [cells, statuses] = await Promise.all([
                    api.getMonthView(start, end),
                    api.getStatusTypes(start, end),
                ]);
                dispatch({ type: ST_SET_STATUSES, payload: statuses });
                dispatch({ type: ST_SET_MONTH_GRID, payload: { monthKey: monthKeyOf(selectedMonth), cells } });
                // Flag-metadata (GPS checklist info button) is fetched lazily on modal open — see
                // fetchFlagMetadata — so it stays off the month-load critical path.
            }
            dispatch({ type: ST_SET_V2_ENABLED, payload: true });
        } catch (e) {
            if (e?.response?.status === 403) {
                dispatch({ type: ST_SET_V2_ENABLED, payload: false });
                if (!silentGate) message.warning("Status Tracker V2 is not enabled for this team yet.");
            } else {
                message.error(errMsg(e, "Failed to load the status tracker."));
            }
        }
        dispatch({ type: ST_SET_LOADING, payload: false });
    };

    // GPS-checklist metadata for the info-button popover — its only consumer. Fetched once per month,
    // lazily, when a cell modal first opens (kept off the grid's critical path).
    const fetchFlagMetadata = async (selectedMonth, monthKey) => {
        // Mock has no flag metadata — mark the month loaded so the info icon doesn't spin forever.
        if (USE_MOCK) {
            dispatch({ type: ST_SET_FLAG_METADATA, payload: { meta: {}, monthKey } });
            return;
        }
        try {
            const meta = await trainingAPI.getStatusTrackerFlagMetadata(
                selectedMonth.format("YYYY"),
                selectedMonth.format("MM")
            );
            dispatch({ type: ST_SET_FLAG_METADATA, payload: { meta, monthKey } });
        } catch (e) {
            console.error(e);
            // Mark loaded even on failure so the info icon stops spinning (falls back to default checklist).
            dispatch({ type: ST_SET_FLAG_METADATA, payload: { meta: {}, monthKey } });
        }
    };

    // One day's cells for the bulk dialog's single-day view. Returned, not stored: the dialog can
    // pick any date, and the tracker's cache stays on the tracker's month. null after a failure,
    // so the dialog keeps its table locked instead of showing a day with no tags.
    const fetchDayCells = async (date) => {
        if (USE_MOCK) return {}; // ponytail: mock cells never carry coach tags
        try {
            const cells = await api.getMonthView(date, date);
            if (!cells || typeof cells !== "object" || Array.isArray(cells)) {
                throw new Error(`Unexpected month-view response for ${date}`);
            }
            return cells;
        } catch (e) {
            message.error(errMsg(e, `Could not load the flags for ${dayjs(date).format("MMM D")}. Try again.`));
            return null;
        }
    };

    // Status assign/clear → unchanged v1 select-status endpoint, then patch the one cell (no refetch).
    const assignStatus = async ({ uniqueKey, player_id, date, status_id }) => {
        if (USE_MOCK) return; // mock select handled by patchCellStatus directly
        try {
            const data = await api.assignPlayerStatus({ status_id, player_id, date });
            patchCell(uniqueKey, data);
        } catch (e) {
            message.error(errMsg(e, "Failed to set status."));
        }
    };

    const clearStatus = async ({ uniqueKey, player_id, date, status_id }) => {
        if (USE_MOCK) return;
        try {
            const data = await api.clearPlayerStatus({ status_id, player_id, date });
            patchCell(uniqueKey, data);
        } catch (e) {
            message.error(errMsg(e, "Failed to clear status."));
        }
    };

    const createStatusType = async ({ name, color }, ctx) => {
        if (USE_MOCK) {
            const nextId = Math.max(0, ...ctx.statuses.map((s) => s.id || s.status_id || 0)) + 1;
            setStatuses([...ctx.statuses, { id: nextId, name, color }]);
            return true;
        }
        try {
            await api.createStatusType({ name, color });
            await refreshStatusTypes(ctx.selectedMonth);
            return true;
        } catch (e) {
            message.error(errMsg(e, "Failed to create status."));
            return false;
        }
    };

    const updateStatusType = async (id, { name, color }, ctx) => {
        if (USE_MOCK) {
            setStatuses(ctx.statuses.map((s) => ((s.id || s.status_id) === id ? { ...s, name, color } : s)));
            return true;
        }
        try {
            await api.updateStatusType(id, { name, color });
            // Rename/recolour changes cells' status snapshots → refresh grid + list.
            await fetchMonthGrid({ players: ctx.players, selectedMonth: ctx.selectedMonth, keyMetrics: ctx.keyMetrics });
            return true;
        } catch (e) {
            message.error(errMsg(e, "Failed to update status."));
            return false;
        }
    };

    const deleteStatusType = async (id, ctx) => {
        if (USE_MOCK) {
            setStatuses(ctx.statuses.filter((s) => (s.id || s.status_id) !== id));
            return true;
        }
        try {
            await api.deleteStatusType(id, ctx.playerId);
            await fetchMonthGrid({ players: ctx.players, selectedMonth: ctx.selectedMonth, keyMetrics: ctx.keyMetrics });
            return true;
        } catch (e) {
            message.error(errMsg(e, "Failed to delete status.")); // 409 → "Status in use by N player(s)"
            return false;
        }
    };

    const setTaggingMode = (mode) => dispatch({ type: ST_SET_TAGGING_MODE, payload: mode });

    // Tag one player on one date, then patch that one cell with the entry the server persisted
    // — the month view never returns coach rows, so a refetch would drop the flag, not show it.
    const saveCoachFlagTag = async ({ uniqueKey, flag, notes }) => {
        const [playerId, date] = uniqueKey.split("_");
        try {
            const result = await api.tagCoachFlag({
                player_id: Number(playerId),
                flag_id: flag.id,
                date,
                notes: notes?.trim() || null,
            });
            const saved = result?.tags?.[0];
            if (!saved) {
                message.error("The flag was saved but could not be shown. Reload to see it.");
                return false;
            }
            dispatch({ type: ST_ADD_COACH_FLAG, payload: { uniqueKey, flag: saved } });
            // Tagging mode stays on: a coach tags several cells in a row, and only the
            // banner's Cancel or the menu toggle leaves it.
            message.success("Flag added");
            return true;
        } catch (e) {
            message.error(errMsg(e, "Failed to add the flag."));
            return false;
        }
    };

    // Untag one player on one date. Which entry goes is fully determined by the flag id and
    // the cell, so the response is the success signal; there is nothing to read back.
    const deleteCoachFlagTag = async ({ uniqueKey, flagId }) => {
        const [playerId, date] = uniqueKey.split("_");
        try {
            await api.untagCoachFlag({ player_id: Number(playerId), flag_id: flagId, date });
            dispatch({ type: ST_REMOVE_COACH_FLAG, payload: { uniqueKey, flagId } });
            message.success("Flag removed");
            return true;
        } catch (e) {
            message.error(errMsg(e, "Failed to remove the flag."));
            return false;
        }
    };

    // Apply a bulk edit: tag the cells that changed, untag the ones that were unchecked.
    // Each half patches only its own cells, so a failure in the second leaves the first
    // applied on screen exactly as it is in the database.
    const saveCoachFlagTagsBulk = async ({ flag, writes = [], removes = [] }) => {
        let written = 0;
        try {
            if (writes.length > 0) {
                const result = await api.tagCoachFlagsBulk({ flag_id: flag.id, targets: writes });
                const tags = result?.tags;
                if (!Array.isArray(tags) || tags.length === 0) {
                    message.error("The flags were saved but could not be shown. Reload to see them.");
                    return false;
                }
                dispatch({ type: ST_ADD_COACH_FLAGS, payload: tags });
                written = tags.length;
            }
            if (removes.length > 0) {
                await api.untagCoachFlagsBulk({ flag_id: flag.id, targets: removes });
                dispatch({
                    type: ST_REMOVE_COACH_FLAGS,
                    payload: {
                        flagId: flag.id,
                        uniqueKeys: removes.map((c) => `${c.player_id}_${c.date}`),
                    },
                });
            }
            const parts = [];
            if (written > 0) parts.push(`${written} tagged`);
            if (removes.length > 0) parts.push(`${removes.length} removed`);
            message.success(parts.join(", ") || "Nothing to save");
            return true;
        } catch (e) {
            message.error(errMsg(e, "Failed to save the flags."));
            return false;
        }
    };

    return {
        setTaggingMode,
        saveCoachFlagTag,
        deleteCoachFlagTag,
        saveCoachFlagTagsBulk,
        fetchMonthGrid,
        fetchDayCells,
        fetchFlagMetadata,
        refreshStatusTypes,
        patchCellStatus: patchCell,
        setStatuses,
        assignStatus,
        clearStatus,
        createStatusType,
        updateStatusType,
        deleteStatusType,
    };
};
